// @refresh reset
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { GUEST_SESSION, restoreCustomerSession, type Customer, type CustomerSession } from '@/lib/customerSession';
import { scheduleNonCriticalTask } from '@/lib/nonCriticalTask';
import { isPrerenderMode } from '@/lib/isPrerenderMode';

export type { Customer } from '@/lib/customerSession';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface CustomerContextValue {
  customer: Customer | null;
  accessToken: string | null;
  isLoading: boolean;
  sessionType: "local" | "oauth" | null;
  ensureSession: () => Promise<CustomerSession>;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string | null, customer: Customer) => void;
  restoreOAuthSession: () => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  patchCustomer: (partial: Partial<Customer>) => void;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [sessionType, setSessionType] = useState<"local" | "oauth" | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const currentSession = useRef<CustomerSession>(GUEST_SESSION);
  const sessionRequest = useRef<Promise<CustomerSession> | null>(null);
  const sessionResolved = useRef(false);
  const sessionRevision = useRef(0);
  const mounted = useRef(false);

  const applySession = useCallback((session: CustomerSession) => {
    currentSession.current = session;
    sessionResolved.current = true;
    setCustomer(session.customer);
    setAccessToken(session.accessToken);
    setSessionType(session.sessionType);
    setIsLoading(false);
  }, []);

  const ensureSession = useCallback((): Promise<CustomerSession> => {
    if (sessionResolved.current) return Promise.resolve(currentSession.current);
    if (!sessionRequest.current) {
      const revision = sessionRevision.current;
      sessionRequest.current = restoreCustomerSession(BASE).then(session => {
        // Signing in/out while a restore is in flight must win over that stale response.
        if (revision === sessionRevision.current && mounted.current) applySession(session);
        return currentSession.current;
      }).finally(() => { sessionRequest.current = null; });
    }
    return sessionRequest.current;
  }, [applySession]);

  useEffect(() => {
    mounted.current = true;
    if (isPrerenderMode) {
      applySession(GUEST_SESSION);
      return () => { mounted.current = false; };
    }
    const restore = () => { void ensureSession(); };
    const needsSession = /\/(konto|checkout|reset-hasla|dziekujemy)\/?$/.test(window.location.pathname);
    const cancel = needsSession ? (restore(), () => {}) : scheduleNonCriticalTask(restore);
    return () => { mounted.current = false; cancel(); };
  }, [applySession, ensureSession]);

  const loginWithToken = useCallback((token: string | null, customer: Customer) => {
    sessionRevision.current += 1;
    applySession({ accessToken: token, customer, sessionType: 'local' });
  }, [applySession]);

  const restoreOAuthSession = useCallback(async () => {
    // Explicit Google sign-in supersedes any anonymous startup check in flight.
    const revision = ++sessionRevision.current;
    const response = await fetch(`${BASE}/api/auth/session`, { credentials: "include", cache: "no-store" });
    const data = await response.json() as { customer?: Customer | null };
    if (!response.ok || !data.customer) throw new Error("Nie udało się potwierdzić logowania. Spróbuj ponownie.");
    if (revision === sessionRevision.current && mounted.current) {
      applySession({ accessToken: null, customer: data.customer, sessionType: 'oauth' });
    }
  }, [applySession]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${BASE}/api/shopify/customer/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json() as { error?: string; authenticated?: boolean; customer?: Customer };
    if (!res.ok || data.error) throw new Error(data.error ?? "Błąd logowania");
    if (!data.authenticated || !data.customer) throw new Error("Nie udało się potwierdzić logowania.");
    loginWithToken(null, data.customer);
  }, [loginWithToken]);

  const register = useCallback(async (firstName: string, lastName: string, email: string, password: string, phone?: string) => {
    const res = await fetch(`${BASE}/api/shopify/customer/register`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ firstName, lastName, email, password, ...(phone ? { phone } : {}) }),
    });
    const data = await res.json() as { error?: string; authenticated?: boolean; customer?: Customer };
    if (!res.ok || data.error) throw new Error(data.error ?? "Błąd rejestracji");
    if (data.authenticated && data.customer) {
      sessionRevision.current += 1;
      applySession({
        accessToken: null, sessionType: 'local', customer: data.customer,
      });
    }
  }, [applySession]);

  const patchCustomer = useCallback((partial: Partial<Customer>) => {
    const previous = currentSession.current;
    if (previous.customer) {
      const next = { ...previous, customer: { ...previous.customer, ...partial } };
      currentSession.current = next;
      setCustomer(next.customer);
    }
  }, []);

  const logout = useCallback(async () => {
    sessionRevision.current += 1;
    // If OAuth session, clear the HttpOnly cookie via server endpoint
    if (sessionType === "oauth") {
      await fetch(`${BASE}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        // Wymagane przez backend jako ochrona CSRF (patrz csrf.ts) — zwykły
        // <form> na obcej stronie nie potrafi ustawić tego nagłówka.
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }).catch(() => null);
    }

    // If local session, delete Storefront API token
    if (sessionType === "local") {
      await fetch(`${BASE}/api/shopify/customer/token`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify(accessToken ? { accessToken } : {}),
      }).catch(() => null);
    }

    applySession(GUEST_SESSION);
  }, [accessToken, sessionType, applySession]);

  return (
    <CustomerContext.Provider value={{ customer, accessToken, isLoading, sessionType, ensureSession, login, loginWithToken, restoreOAuthSession, register, logout, patchCustomer }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used inside CustomerProvider");
  return ctx;
}
